/**
 * AddFromOrgModal - FT-05: Add crew from organisation roster
 *
 * Shows crew NOT assigned to current vessel, allows assignment.
 * Uses useFleetCrew for org-scoped crew data.
 */

import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, UserPlus, Users, Ship } from 'lucide-react';
import { useVessel } from 'contexts/VesselContext';
import { useFleetCrew } from '../../../../hooks/domain/fleet/useFleetCrew';

const AddFromOrgModal = ({ isOpen, onClose, onAssign }) => {
  const { currentVesselId, getCurrentVessel } = useVessel();
  const { list: allCrew, assignToVessel } = useFleetCrew();
  const currentVessel = getCurrentVessel();

  const [searchTerm, setSearchTerm] = useState('');
  const [isAssigning, setIsAssigning] = useState(null); // Track which crew is being assigned

  // Get crew NOT assigned to current vessel (excludes archived)
  const unassignedCrew = useMemo(() => {
    if (!currentVesselId) return [];
    return allCrew.filter(member => {
      // Skip archived
      if (member.status === 'archived') return false;
      // Skip if already assigned to current vessel
      const isHomeVessel = member.homeVessel === currentVesselId;
      const isAssigned = (member.assignedVessels || []).includes(currentVesselId);
      return !isHomeVessel && !isAssigned;
    });
  }, [allCrew, currentVesselId]);

  // Filter by search
  const filteredCrew = useMemo(() => {
    if (!searchTerm.trim()) return unassignedCrew;
    const term = searchTerm.toLowerCase();
    return unassignedCrew.filter(member =>
      (member.name || '').toLowerCase().includes(term) ||
      (member.position || member.rank || '').toLowerCase().includes(term)
    );
  }, [unassignedCrew, searchTerm]);

  // Handle assign crew to current vessel
  const handleAssign = async (crewMember) => {
    setIsAssigning(crewMember.id);
    try {
      const result = assignToVessel(crewMember.id, currentVesselId);
      if (result && onAssign) {
        onAssign(crewMember);
      }
    } finally {
      setIsAssigning(null);
    }
  };

  if (!isOpen) return null;

  const modalRoot = document.getElementById('modal-root');

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="sticky top-0 bg-slate-700 rounded-t-lg px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-white" />
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Add from Organisation
                </h2>
                <p className="text-sm text-slate-300">
                  Assign crew to {currentVessel?.name || 'this vessel'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-300 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or position..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>

          {/* Crew List */}
          <div className="flex-1 overflow-y-auto p-4">
            {filteredCrew.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">
                  {unassignedCrew.length === 0
                    ? 'All crew members are assigned to this vessel'
                    : 'No crew members match your search'}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {unassignedCrew.length === 0
                    ? 'Add new crew to the organisation first'
                    : 'Try a different search term'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredCrew.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                        <span className="text-cyan-700 font-medium text-sm">
                          {(member.name || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{member.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{member.position || member.rank || 'No position'}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleAssign(member)}
                      disabled={isAssigning === member.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isAssigning === member.id ? (
                        <>
                          <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Assigning...
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4" />
                          Assign
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-lg flex justify-between items-center">
            <p className="text-sm text-gray-500">
              <Ship className="h-4 w-4 inline mr-1" />
              {unassignedCrew.length} crew available to assign
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal if available, otherwise render inline
  return modalRoot ? createPortal(modalContent, modalRoot) : modalContent;
};

export default AddFromOrgModal;
